// HTTP implementation of the AuthPort. Talks to `theplaidoyer-api`
// via `POST /api/v1/auth/login` + `POST /api/v1/auth/logout`.
//
// The BE returns a `TokenResponse` body AND sets HttpOnly cookies
// (`access_token`, `refresh_token`, `session_id`) in the same response.
// In the SaaS the FE relies on cookies (`credentials: 'include'`). In
// the Office iframe sandbox third-party cookies are sometimes blocked
// (notably Word for Mac 16.x), so we ALSO persist the access_token in
// localStorage and stand ready to attach `Authorization: Bearer …` on
// PR-3+ requests. Defense in depth.
//
// SYNC.md note: this adapter is NOT a verbatim copy of any FE file —
// the FE uses a Zod-validated `ApiClient` class; the add-in uses a
// minimal hand-written fetch wrapper for now (no zod dep yet). When
// PR-3 starts copying `src/adapters/api-client/*` from FE, this file
// will be folded into the generated client.

import type { AuthPort } from '@/domain/auth/ports'
import {
  AuthError,
  type AuthSession,
  type Credentials,
} from '@/domain/auth/entity'

export interface AuthHttpConfig {
  baseUrl: string
  fetch?: typeof fetch
}

interface RawTokenResponse {
  access_token: string
  token_type: string
  user_id: string
  email: string
  is_admin?: boolean
  is_owner?: boolean
}

/** Translate a fetch response to an AuthError discriminant. 401 is the
 *  hot path — every other code routes to network/unknown. */
async function toAuthError(res: Response): Promise<AuthError> {
  let detail = ''
  try {
    const body = (await res.json()) as { detail?: unknown }
    if (typeof body.detail === 'string') detail = body.detail
  } catch {
    // body was not JSON — fall through to status-based message
  }
  if (res.status === 401) {
    return new AuthError(
      'invalid_credentials',
      detail || 'Identifiants invalides',
    )
  }
  if (res.status === 429) {
    return new AuthError(
      'rate_limited',
      detail || 'Trop de tentatives, réessayez dans quelques instants',
    )
  }
  return new AuthError('unknown', detail || `HTTP ${res.status}`)
}

export function createAuthHttp(config: AuthHttpConfig): AuthPort {
  const doFetch =
    config.fetch ?? ((input, init) => globalThis.fetch(input, init))
  const url = (path: string): string => `${config.baseUrl}${path}`

  return {
    async login(credentials: Credentials): Promise<AuthSession> {
      let res: Response
      try {
        res = await doFetch(url('/api/v1/auth/login'), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({
            email: credentials.email,
            password: credentials.password,
          }),
        })
      } catch (err) {
        throw new AuthError(
          'network',
          err instanceof Error ? err.message : 'Réseau injoignable',
        )
      }
      if (!res.ok) {
        throw await toAuthError(res)
      }
      const body = (await res.json()) as RawTokenResponse
      return {
        accessToken: body.access_token,
        userId: body.user_id,
        email: body.email,
        isAdmin: body.is_admin ?? false,
        isOwner: body.is_owner ?? false,
      }
    },
    async logout(): Promise<void> {
      try {
        await doFetch(url('/api/v1/auth/logout'), {
          method: 'POST',
          credentials: 'include',
        })
      } catch {
        // Best-effort — caller (signOut usecase) ignores failures.
      }
    },
  }
}
